import {
  Configuration,
  GetPlacesByNameRequest,
  GetTripsByOriginAndDestinationAcceptLanguageEnum,
  GetTripsByOriginAndDestinationRequest,
  PlaceResponse,
  PlacesV3Api,
  SchedulesV3Api,
  ServiceCalendar,
  TripResponse,
  TripsV3Api,
} from '../api/clients/journey-service';

import {
  PostTripPricesAcceptLanguageEnum,
  PostTripPricesRequest,
  PriceApi,
  Configuration as SmapiConfig,
  TripPrice,
} from '../api/clients/smapi-ticketing';

// ------------------------
// Places request
// ------------------------

const placesLive = async (api: PlacesV3Api, params: GetPlacesByNameRequest, lang: GetTripsByOriginAndDestinationAcceptLanguageEnum): Promise<PlaceResponse> => {
  const fetchedPlaces = await api.getPlacesByName({
    ...params,
    acceptLanguage: lang,
  });

  return fetchedPlaces;
};

const placesMock = async (mocksDir: string): Promise<PlaceResponse> => {
  const url = `${mocksDir}places.json`;
  const fetchedPlaces = await fetch(url);
  const fetchedJson = await fetchedPlaces.json();

  return fetchedJson as PlaceResponse;
};

// ------------------------
// Trips request
// ------------------------

const tripsLive = async (api: TripsV3Api, params: GetTripsByOriginAndDestinationRequest, lang: GetTripsByOriginAndDestinationAcceptLanguageEnum): Promise<TripResponse> => {
  const fetchedTrips = await api.getTripsByOriginAndDestination({
    ...params,
    acceptLanguage: lang,
  });

  return fetchedTrips;
};

const tripsMock = async (mocksDir: string): Promise<TripResponse> => {
  const url = `${mocksDir}trips.json`;
  const fetchedTrips = await fetch(url);
  const fetchedJson = await fetchedTrips.json();

  return fetchedJson as TripResponse;
};

// ------------------------
// Price request
// ------------------------

const pricesLive = async (api: PriceApi, params: PostTripPricesRequest, lang: PostTripPricesAcceptLanguageEnum): Promise<TripPrice[]> => {
  const fetchedPrices = await api.postTripPrices({
    ...params,
    acceptLanguage: lang,
  });

  return fetchedPrices;
};

const pricesMock = async (mocksDir: string): Promise<TripPrice[]> => {
  const url = `${mocksDir}prices.json`;
  const fetchedTrips = await fetch(url);
  const fetchedJson = await fetchedTrips.json();

  return fetchedJson as TripPrice[];
};

// ------------------------
// Timetable Period request
// ------------------------

const timetablePeriodLive = async (api: SchedulesV3Api): Promise<ServiceCalendar> => {
  const fetchedPeriod = await api.getServiceCalendar();

  return fetchedPeriod;
};

const timetablePeriodMock = async (mocksDir: string): Promise<ServiceCalendar> => {
  const url = `${mocksDir}service-calendar.json`;
  const fetchedPeriod = await fetch(url);
  const fetchedJson = await fetchedPeriod.json();

  return fetchedJson;
};

export class ApiRequestor {
  private _mock: boolean;
  private _mocksDir: string;
  private _placesApi: PlacesV3Api;
  private _tripsApi: TripsV3Api;
  private _pricesApi: PriceApi;
  private _schedulesApi: SchedulesV3Api;

  public constructor(
    journeyServiceApiUrl,
    smapiUrl,
    mock,
  ) {
    this._mock = mock;

    const journeyServiceRequestConfig = new Configuration({
      basePath: journeyServiceApiUrl,
    });

    const smapiConfig = new SmapiConfig({
      basePath: smapiUrl,
    });

    // /mocks/ -> works for stencil (important for tests)
    // /www/mocks/ -> works for storybook
    this._mocksDir = 'mocks/';

    // a bit hacky, but does the trick...
    if (window.location.port === '6006') {
      this._mocksDir = 'www/mocks/';
    }

    this._placesApi = new PlacesV3Api(journeyServiceRequestConfig);
    this._tripsApi = new TripsV3Api(journeyServiceRequestConfig);
    this._pricesApi = new PriceApi(smapiConfig);
    this._schedulesApi = new SchedulesV3Api(journeyServiceRequestConfig);
  }

  public trips(params: GetTripsByOriginAndDestinationRequest, lang: string): Promise<TripResponse> {
    return (this._mock
      ? tripsMock(this._mocksDir)
      : tripsLive(this._tripsApi, params, lang as GetTripsByOriginAndDestinationAcceptLanguageEnum)
    );
  }

  public places(params: GetPlacesByNameRequest, lang: string): Promise<PlaceResponse> {
    return (this._mock
      ? placesMock(this._mocksDir)
      : placesLive(this._placesApi, params, lang as GetTripsByOriginAndDestinationAcceptLanguageEnum)
    );
  }

  public prices(params: PostTripPricesRequest, lang: string): Promise<TripPrice[]> {
    return (this._mock
      ? pricesMock(this._mocksDir)
      : pricesLive(this._pricesApi, params, lang as PostTripPricesAcceptLanguageEnum)
    );
  }

  public timetablePeriod(): Promise<ServiceCalendar> {
    return (this._mock
      ? timetablePeriodMock(this._mocksDir)
      : timetablePeriodLive(this._schedulesApi)
    );
  }
}
